import { useMemo } from 'react';
import { useTradingStrategies } from '@/hooks/useTradingStrategies';
import SearchSelect, { type SearchSelectFormProps, type SearchSelectItem } from './SearchSelect';
import { buildTradingStrategySelectItems, type TradingStrategySelectItemOptions } from './tradingStrategySelectItems';

export interface TradingStrategySelectProps extends SearchSelectFormProps, TradingStrategySelectItemOptions {
    value: number | null;
    onChange: (strategyId: number | null) => void;
}

const missingItem = (strategyId: number): SearchSelectItem => ({
    label: `Strategy ${strategyId} (#${strategyId})`,
    meta: 'Not in your current strategy list',
    name: `Strategy ${strategyId}`,
    value: strategyId
});

/**
 * Searchable single select for trading strategies. Fetches its own data via
 * useTradingStrategies, so call sites only manage the selected id. Search
 * matches name, id (with or without #), duration, and description.
 */
const TradingStrategySelect = (props: TradingStrategySelectProps) => {
    const { value, onChange, filter, getUnavailableReason, placeholder, ...formProps } = props;

    const { error: strategiesError, isLoading, strategies } = useTradingStrategies();

    const items = useMemo(
        () => buildTradingStrategySelectItems(strategies, { filter, getUnavailableReason }),
        [filter, getUnavailableReason, strategies]
    );

    const loadError = strategiesError ? `Failed to load trading strategies: ${strategiesError.message}` : undefined;

    return (
        <SearchSelect
            {...formProps}
            isLoading={isLoading}
            items={items}
            loadError={loadError}
            mode="single"
            placeholder={placeholder ?? 'Select a strategy...'}
            resolveMissingItem={missingItem}
            value={value}
            onChange={onChange}
        />
    );
};

export default TradingStrategySelect;
